export interface Logger {
  info: typeof console.info;
  log: typeof console.log;
  error: typeof console.error;
  warn: typeof console.warn;
}

export class ServerSentEventClient {
  eventSource: EventSource | null = null;
  logName: string = '';
  logger: Logger = console;
  sourceUrl: string = '';
  errorCount = 0;
  openPromise: Promise<any> | null = null;
  reconnectionThresold = 5;

  constructor(url: string, logger: Logger, logname: string) {
    this.sourceUrl = url;
    this.logName = logname;
    this.logger = logger;
    if (window.addEventListener) {
      window.addEventListener('beforeunload', () => {
        if (this.eventSource) {
          this.eventSource.close();
        }
      });
    }
  }

  connect(): Promise<any> {
    this.logger.info(`SSE-${this.logName}: Starting SSE connection`);
    this.openPromise = this.preConnect()
      .then(() => {
        try {
          this.eventSource = new EventSource(this.sourceUrl);
          this.logger.info(`SSE-${this.logName}: Call made for opening sse request to server at ${this.sourceUrl}`);
          (window as any)[this.logName] = this;

          return new Promise((resolve, reject) => {
            if (this.eventSource) {
              this.eventSource.addEventListener('open', event => {
                resolve(event);
                this._onOpened();
              });
              this.eventSource.addEventListener('error', err => {
                this.logger.warn('SSE: connection error', JSON.stringify(err));
                this._onError().then(resolve);
              });
              this.eventSource.addEventListener('message', this._onMessage.bind(this));
              this.eventSource.addEventListener('ping', this._onPing.bind(this));
            } else {
              reject(new Error('Cannot attach open and error event handlers on event-source as its null!'));
            }
          });
        } catch (e: any) {
          this.logger.error(`SSE-${this.logName}: Error during connecting with the server`, JSON.stringify(e));
          throw e;
        }
      })
      .catch(err => {
        this.logger.error(`SSE-${this.logName}: Error during connecting with the server`, JSON.stringify(err));
      });
    return this.openPromise;
  }

  _onOpened() {
    this.logger.info(`SSE-${this.logName}: eventsource opened`);
    this.errorCount = 0;
    if (this.eventSource) {
      this.eventSource.close = () => this._onClosed();
    } else {
      throw new Error(`SSE-${this.logName}: Cannot set onclosed for reconnection because there is no eventsource`);
    }
    this.onOpened();
  }

  _onClosed() {
    this.logger.info(`SSE-${this.logName}: eventsource connection closed`);
    if (this.eventSource) {
      this.eventSource.removeEventListener('error', this._onError);
      this.eventSource.close = () => null;
    }
    this.eventSource = null;
    this.openPromise = null;
    this.onClosed();
  }

  _onError() {
    this.logger.error(`SSE-${this.logName}: eventsource connection has errored out!`);
    this.errorCount++;

    if (this.errorCount > this.reconnectionThresold) {
      this.logger.error(`SSE-${this.logName}: has errored out! Bailing out!`);
      this.close();
      this.onBail();
      return Promise.resolve();
    }

    this.onError(this.errorCount);
    return Promise.resolve();
  }

  _onMessage(event: any) {
    this.logger.info(`SSE-${this.logName}: message-> ${event?.data}`);
    // extend this when using actually in application
    this.onMessage(event);
  }
  _onPing(event) {
    this.logger.info(`SSE-${this.logName}: ping from server, ${event?.data}}`);
    this.onPing();
  }

  close() {
    this.eventSource?.close();
  }

  // To be overridden by concrete classes
  preConnect() {
    return Promise.resolve();
  }

  // eslint-disable-next-line no-unused-vars
  onError(connectionAttempt: number) {
    this.logger.warn('onError not implemented');
  }

  //eslint-disable-next-line
  onMessage(event: any) {
    this.logger.warn('onMessage not implemented');
  }

  onPing() {
    // implement ping logic - server sent heartbeat
  }

  onClosed() {
    this.logger.warn('onClose not implemented');
  }

  onOpened() {
    this.logger.warn('onOpened not implemented');
  }

  onBail() {
    this.logger.warn('onBail not implemented');
  }
}
